// RecadoView — editor do recado (anúncio) global da plataforma.
//
// Funcionalidades portadas do legado (manager/index.html aba "recado"):
//   - Toggle enabled
//   - Tone radio (info/warning/success/danger/neutral) — 5 opções
//   - Title (até 80 chars) + Text (até 600 chars)
//   - Audiência: all (todo mundo logado) | restricted (lista de emails)
//   - Preview live ao lado do editor
//   - Histórico das últimas N versões (read-only)
//
// Não portado nesta primeira passada:
//   - audience.groupIds (depende de manager-groups endpoint ainda não portado)
//
// Endpoint: GET /api/manager/announcement | GET ?history=1 | PUT
// Persistência: tabela platform_config, chaves "announcement" e
// "announcement-history" (em apps/api/src/functions/manager-announcement.ts).

import { signal, computed } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  History,
  Info,
  Loader2,
  Megaphone,
  Plus,
  Save,
  Trash2,
  XCircle
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type AnnouncementRecord,
  type AnnouncementAudience
} from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

type Tone = "info" | "warning" | "success" | "danger" | "neutral";

const TONES: { value: Tone; label: string; icon: typeof Info; color: string }[] = [
  { value: "info", label: "Informativo", icon: Info, color: "sky" },
  { value: "warning", label: "Atenção", icon: AlertTriangle, color: "amber" },
  { value: "success", label: "Sucesso", icon: CheckCircle2, color: "emerald" },
  { value: "danger", label: "Crítico", icon: AlertCircle, color: "red" },
  { value: "neutral", label: "Neutro", icon: Megaphone, color: "slate" }
];

const MAX_TITLE = 80;
const MAX_TEXT = 600;

// State local da view (não vai pra signals globais — view re-mount = reset).
const recadoData = signal<AnnouncementRecord | null>(null);
const recadoPhase = signal<"loading" | "ready" | "error">("loading");
const recadoErr = signal<{ message: string; stack: string | null; status: number | null } | null>(
  null
);
const saving = signal<boolean>(false);
const lastSavedAt = signal<number | null>(null);
const historyOpen = signal<boolean>(false);
const historyList = signal<AnnouncementRecord[] | null>(null);
const historyLoading = signal<boolean>(false);

async function loadRecado() {
  recadoPhase.value = "loading";
  recadoErr.value = null;
  try {
    const rec = await api.getAnnouncement();
    recadoData.value = rec;
    recadoPhase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    recadoErr.value = {
      message: friendlyError(err),
      stack: err instanceof Error ? err.stack ?? null : null,
      status: err instanceof ApiError ? err.status : null
    };
    recadoPhase.value = "error";
  }
}

async function loadHistory() {
  historyLoading.value = true;
  try {
    const res = await api.getAnnouncementHistory();
    historyList.value = res.history;
  } catch (err) {
    historyList.value = [];
  } finally {
    historyLoading.value = false;
  }
}

export function RecadoView() {
  useEffect(() => {
    loadRecado();
  }, []);
  const ph = recadoPhase.value;
  if (ph === "loading") return <RecadoLoading />;
  if (ph === "error") return <RecadoError err={recadoErr.value} />;
  return <RecadoEditor initial={recadoData.value!} />;
}

function RecadoLoading() {
  return (
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Skeleton className="h-[420px] rounded-md" />
      <Skeleton className="h-[420px] rounded-md" />
    </div>
  );
}

function RecadoError({
  err
}: {
  err: { message: string; stack: string | null; status: number | null } | null;
}) {
  return (
    <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
      <div class="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
        <div class="min-w-0 flex-1 space-y-1.5">
          <h3 class="text-sm font-bold">
            Não consegui carregar o recado
            {err?.status ? (
              <span class="ml-2 rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-destructive">
                HTTP {err.status}
              </span>
            ) : null}
          </h3>
          {err?.message && (
            <p class="text-xs text-muted-foreground">{err.message}</p>
          )}
          {err?.stack && (
            <details class="text-[11px]">
              <summary class="cursor-pointer text-muted-foreground/80 hover:text-foreground">
                Ver stack trace
              </summary>
              <pre class="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-background/60 p-2 font-mono text-[10px] leading-relaxed">
                {err.stack}
              </pre>
            </details>
          )}
          <button
            type="button"
            onClick={() => loadRecado()}
            class="mt-1 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  );
}

function RecadoEditor({ initial }: { initial: AnnouncementRecord }) {
  const [enabled, setEnabled] = useState(initial.enabled);
  const [title, setTitle] = useState(initial.title);
  const [text, setText] = useState(initial.text);
  const [tone, setTone] = useState<Tone>(initial.tone as Tone);
  const [audienceMode, setAudienceMode] = useState<"all" | "restricted">(
    initial.audience.mode
  );
  const [audienceEmails, setAudienceEmails] = useState<string[]>(
    initial.audience.emails
  );
  const [emailDraft, setEmailDraft] = useState("");

  function addEmail() {
    const e = emailDraft.trim().toLowerCase();
    if (!e || !e.includes("@")) return;
    if (audienceEmails.includes(e)) {
      setEmailDraft("");
      return;
    }
    setAudienceEmails([...audienceEmails, e]);
    setEmailDraft("");
  }
  function removeEmail(e: string) {
    setAudienceEmails(audienceEmails.filter((x) => x !== e));
  }

  async function handleSave() {
    saving.value = true;
    try {
      const audience: AnnouncementAudience = {
        mode: audienceMode,
        emails: audienceEmails,
        groupIds: initial.audience.groupIds // preserva (groups não portado ainda)
      };
      const saved = await api.saveAnnouncement({
        enabled,
        title,
        text,
        tone,
        audience
      });
      recadoData.value = saved;
      lastSavedAt.value = saved.savedAt;
      // Sincroniza estados locais com o que o server devolveu (caso tenha
      // truncado/normalizado algo).
      setEnabled(saved.enabled);
      setTitle(saved.title);
      setText(saved.text);
      setTone(saved.tone as Tone);
      setAudienceMode(saved.audience.mode);
      setAudienceEmails(saved.audience.emails);
    } catch (err) {
      void openAlert({
        title: "Erro",
        message:
          err instanceof Error ? `Falha ao salvar: ${err.message}` : "Falha ao salvar."
      });
    } finally {
      saving.value = false;
    }
  }

  return (
    <div class="space-y-4">
      {/* Linha de status + ação */}
      <div class="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card/40 px-4 py-3">
        <button
          type="button"
          onClick={() => setEnabled(!enabled)}
          class={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors ${
            enabled
              ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              : "border-border bg-background text-muted-foreground hover:bg-accent"
          }`}
        >
          <span
            class={`size-2.5 rounded-full ${enabled ? "bg-emerald-500" : "bg-muted-foreground/40"}`}
          />
          {enabled ? "Recado ativo" : "Recado desativado"}
        </button>

        <span class="hidden text-xs text-muted-foreground sm:inline">
          {enabled
            ? "Visível para a audiência selecionada na home dos usuários."
            : "Nenhum aluno verá esse recado até você ativar."}
        </span>

        <button
          type="button"
          onClick={() => {
            historyOpen.value = !historyOpen.value;
            if (historyOpen.value && historyList.value === null) loadHistory();
          }}
          class="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
        >
          <History width="1.1em" height="1.1em" />
          {historyOpen.value ? "Ocultar histórico" : "Histórico"}
        </button>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving.value}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-bold text-white shadow-sm transition-colors hover:brightness-110 disabled:opacity-60"
        >
          {saving.value ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Save width="1.1em" height="1.1em" />
          )}
          Salvar
        </button>
      </div>

      {lastSavedAt.value && (
        <div class="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 width="1.1em" height="1.1em" />
          Salvo {formatRelative(lastSavedAt.value)}
        </div>
      )}

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Coluna 1 — formulário */}
        <div class="space-y-4">
          <FormSection title="Tom" hint="Define a cor e o tom da mensagem">
            <div class="grid grid-cols-5 gap-1.5">
              {TONES.map((t) => (
                <ToneOption
                  key={t.value}
                  meta={t}
                  active={tone === t.value}
                  onPick={() => setTone(t.value)}
                />
              ))}
            </div>
          </FormSection>

          <FormSection
            title="Título"
            hint={`até ${MAX_TITLE} caracteres`}
            counter={`${title.length}/${MAX_TITLE}`}
          >
            <input
              type="text"
              value={title}
              maxLength={MAX_TITLE}
              onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
              placeholder="Ex: Aula de revisão amanhã"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </FormSection>

          <FormSection
            title="Mensagem"
            hint={`até ${MAX_TEXT} caracteres — markdown simples (negrito, links)`}
            counter={`${text.length}/${MAX_TEXT}`}
          >
            <textarea
              value={text}
              maxLength={MAX_TEXT}
              onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
              placeholder="Texto do recado…"
              rows={6}
              class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </FormSection>

          <FormSection
            title="Audiência"
            hint="Quem vai ver esse recado"
          >
            <div class="flex gap-2">
              <AudienceOption
                value="all"
                label="Todos"
                description="Todo mundo logado vê"
                active={audienceMode === "all"}
                onPick={() => setAudienceMode("all")}
              />
              <AudienceOption
                value="restricted"
                label="Restrito"
                description={`${audienceEmails.length} email${audienceEmails.length !== 1 ? "s" : ""}`}
                active={audienceMode === "restricted"}
                onPick={() => setAudienceMode("restricted")}
              />
            </div>
            {audienceMode === "restricted" && (
              <div class="mt-3 space-y-2">
                <div class="flex gap-2">
                  <input
                    type="email"
                    value={emailDraft}
                    onInput={(e) =>
                      setEmailDraft((e.target as HTMLInputElement).value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addEmail();
                      }
                    }}
                    placeholder="aluno@exemplo.com"
                    class="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={addEmail}
                    class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
                  >
                    <Plus width="1.1em" height="1.1em" />
                    Adicionar
                  </button>
                </div>
                {audienceEmails.length > 0 && (
                  <ul class="flex flex-wrap gap-1.5">
                    {audienceEmails.map((e) => (
                      <li
                        key={e}
                        class="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-[11px] text-foreground"
                      >
                        <span class="font-mono">{e}</span>
                        <button
                          type="button"
                          onClick={() => removeEmail(e)}
                          title="Remover"
                          class="text-muted-foreground hover:text-destructive"
                        >
                          <XCircle width="12" height="12" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {initial.audience.groupIds.length > 0 && (
                  <p class="text-[11px] italic text-muted-foreground">
                    + {initial.audience.groupIds.length} grupo
                    {initial.audience.groupIds.length !== 1 ? "s" : ""} (gerenciado em
                    Grupos, ainda não portado)
                  </p>
                )}
              </div>
            )}
          </FormSection>
        </div>

        {/* Coluna 2 — preview + metadados */}
        <div class="space-y-4">
          <FormSection title="Preview" hint="Como o aluno vai ver na home">
            <RecadoPreview
              enabled={enabled}
              title={title}
              text={text}
              tone={tone}
            />
          </FormSection>

          <FormSection title="Última edição">
            <ul class="space-y-1 text-xs text-muted-foreground">
              <li>
                <strong class="font-semibold text-foreground">
                  {initial.savedBy ?? "—"}
                </strong>{" "}
                · {initial.savedAt ? formatDate(initial.savedAt) : "nunca"}
              </li>
              {initial.enabledAt && (
                <li>
                  Ativado em {formatDate(initial.enabledAt)}
                </li>
              )}
            </ul>
          </FormSection>
        </div>
      </div>

      {historyOpen.value && <HistoryPanel />}
    </div>
  );
}

function FormSection({
  title,
  hint,
  counter,
  children
}: {
  title: string;
  hint?: string;
  counter?: string;
  children: any;
}) {
  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 p-4">
      <header class="flex items-center justify-between gap-2">
        <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        <div class="flex items-center gap-2">
          {counter && (
            <span class="text-[10px] tabular-nums text-muted-foreground/70">
              {counter}
            </span>
          )}
          {hint && (
            <span class="text-[10px] text-muted-foreground/70">{hint}</span>
          )}
        </div>
      </header>
      {children}
    </section>
  );
}

function ToneOption({
  meta,
  active,
  onPick
}: {
  meta: { value: Tone; label: string; icon: typeof Info; color: string };
  active: boolean;
  onPick: () => void;
}) {
  const Icon = meta.icon;
  const colorMap: Record<string, string> = {
    sky: "border-sky-500/50 bg-sky-500/10 text-sky-700 dark:text-sky-300",
    amber: "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300",
    emerald:
      "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    red: "border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400",
    slate: "border-border bg-card text-foreground"
  };
  return (
    <button
      type="button"
      onClick={onPick}
      class={`flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-[11px] font-semibold transition-all ${
        active
          ? `${colorMap[meta.color]} ring-2 ring-offset-1 ring-offset-card`
          : "border-border bg-background/50 text-muted-foreground hover:bg-accent"
      }`}
      style={
        active
          ? {
              "--tw-ring-color": "var(--primary)"
            } as any
          : undefined
      }
    >
      <Icon className="size-4" />
      <span class="truncate">{meta.label}</span>
    </button>
  );
}

function AudienceOption({
  value,
  label,
  description,
  active,
  onPick
}: {
  value: string;
  label: string;
  description: string;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      class={`flex-1 rounded-md border px-3 py-2 text-left transition-colors ${
        active
          ? "border-primary/50 bg-primary/10"
          : "border-border bg-background/50 hover:bg-accent"
      }`}
    >
      <div class="text-sm font-bold">{label}</div>
      <div class="text-[11px] text-muted-foreground">{description}</div>
    </button>
  );
}

function RecadoPreview({
  enabled,
  title,
  text,
  tone
}: {
  enabled: boolean;
  title: string;
  text: string;
  tone: Tone;
}) {
  if (!title.trim() && !text.trim()) {
    return (
      <div class="rounded-md border border-dashed border-border bg-background/40 px-4 py-8 text-center text-xs text-muted-foreground">
        Preencha título ou mensagem para visualizar.
      </div>
    );
  }
  const toneMeta = TONES.find((t) => t.value === tone) ?? TONES[0];
  const Icon = toneMeta.icon;
  const colorClasses: Record<Tone, string> = {
    info: "border-sky-500/40 bg-sky-500/10 text-sky-900 dark:text-sky-100",
    warning:
      "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-100",
    success:
      "border-emerald-500/40 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100",
    danger:
      "border-red-500/40 bg-red-500/10 text-red-900 dark:text-red-100",
    neutral: "border-border bg-card text-foreground"
  };
  const iconColors: Record<Tone, string> = {
    info: "text-sky-600 dark:text-sky-400",
    warning: "text-amber-600 dark:text-amber-400",
    success: "text-emerald-600 dark:text-emerald-400",
    danger: "text-red-600 dark:text-red-400",
    neutral: "text-foreground"
  };
  return (
    <div class="space-y-2">
      {!enabled && (
        <div class="flex items-center gap-2 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-3 py-1.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">
          <span class="inline-block size-1.5 rounded-full bg-amber-500" />
          Rascunho — alunos ainda <strong>não veem</strong> este recado. Ative
          o switch acima pra publicar.
        </div>
      )}
      <div
        class={`flex items-start gap-3 rounded-md border-l-4 px-4 py-3 ${
          colorClasses[tone]
        } ${!enabled ? "opacity-80 ring-1 ring-amber-500/30" : ""}`}
      >
        <Icon className={`mt-0.5 size-5 shrink-0 ${iconColors[tone]}`} />
        <div class="min-w-0 flex-1 space-y-1">
          {title && <div class="font-bold leading-tight">{title}</div>}
          {text && (
            <div class="text-sm leading-relaxed whitespace-pre-line opacity-90">
              {text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HistoryPanel() {
  const list = historyList.value;
  const loading = historyLoading.value;
  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 p-4">
      <header class="flex items-center gap-2">
        <History className="size-4 text-muted-foreground" />
        <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Histórico de versões
        </h3>
        <span class="text-[10px] text-muted-foreground/70">
          até as 50 versões mais recentes
        </span>
      </header>
      {loading && (
        <div class="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}
      {!loading && list && list.length === 0 && (
        <p class="px-2 py-3 text-xs text-muted-foreground">
          Nenhuma versão anterior.
        </p>
      )}
      {!loading && list && list.length > 0 && (
        <ul class="space-y-1.5">
          {list.map((rec, i) => (
            <li
              key={`${rec.savedAt ?? i}-${i}`}
              class="flex items-start gap-3 rounded-md border border-border/60 bg-background/40 px-3 py-2"
            >
              <span
                class={`mt-1 size-2 shrink-0 rounded-full ${rec.enabled ? "bg-emerald-500" : "bg-muted-foreground/40"}`}
                title={rec.enabled ? "Estava ativo" : "Estava desativado"}
              />
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-semibold">
                  {rec.title || <em class="italic text-muted-foreground">sem título</em>}
                </div>
                {rec.text && (
                  <div class="line-clamp-2 text-xs text-muted-foreground">
                    {rec.text}
                  </div>
                )}
                <div class="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground/80">
                  <span class="rounded bg-accent px-1.5 py-0.5 font-bold uppercase">
                    {rec.tone}
                  </span>
                  <span>{rec.savedBy ?? "—"}</span>
                  <span class="inline-flex items-center gap-0.5">
                    <Clock width="10" height="10" />
                    {rec.savedAt ? formatDate(rec.savedAt) : "—"}
                  </span>
                  {rec.audience.mode === "restricted" && (
                    <span class="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-700 dark:text-amber-300">
                      restrito · {rec.audience.emails.length}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatDate(ts: number): string {
  const d = new Date(ts);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "agora há pouco";
  if (diff < 3600_000) return `há ${Math.floor(diff / 60_000)} min`;
  if (diff < 86_400_000) return `há ${Math.floor(diff / 3600_000)} h`;
  return `em ${formatDate(ts)}`;
}
